import React, { PropsWithChildren, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { usePortalContext } from '../context/PortalContext';
import { withPostLoginRedirect } from '../services/authRedirect';
import { getAccessPathLabel } from '../utils/accessPath';
import { hasElevatedAccess, PORTAL_ROLES } from '../utils/roles';
import {
  canDeployToAppCatalog as hasDeployCapability,
  canManageClientAccounts as hasClientAccountCapability,
  canReviewQuality,
  canUseAiAdmin,
  canUseCapaRegister,
  canUseRisk
} from '../utils/capabilities';
import { SignOutDialog } from '../components/auth/SignOutDialog';
import { useLanguage } from '../i18n/LanguageContext';
import { LanguageSelector } from '../i18n/LanguageSelector';
import edinshallBadgeIcon from '../assets/edinshall.svg';
import edaudBadgeIcon from '../assets/edaud.svg';
import {
  AppsRegular,
  BeakerRegular,
  BookRegular,
  BuildingRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  DocumentDataRegular,
  HomeRegular,
  MegaphoneRegular,
  PersonRegular,
  PersonSupportRegular,
  SettingsRegular
} from '@fluentui/react-icons';

const edinshallBadgeIconUrl = new URL(edinshallBadgeIcon, import.meta.url).toString();
const edaudBadgeIconUrl = new URL(edaudBadgeIcon, import.meta.url).toString();

const environmentLabel: Record<string, string> = {
  dev: 'Development',
  test: 'Test',
  prod: 'Production'
};

type VisibilitySource = 'employee' | 'audit' | 'mixed';

interface NavItem {
  path: string;
  label: string;
  icon: JSX.Element;
  visibilitySource: VisibilitySource;
}

export const AppLayout = ({ children }: PropsWithChildren): JSX.Element => {
  const { tenant, loading, error, products } = usePortalContext();
  const { t } = useLanguage();
  const [collapsed, setCollapsed] = useState(false);
  const [signOutDialogOpen, setSignOutDialogOpen] = useState(false);
  // After a successful sign-out we land back at `/?signed_out=1`. AppLayout
  // detects this query string and shows a dedicated confirmation card
  // explaining the Edinshall ↔ M365 session boundary before the user
  // signs in again, rather than the default "can't load tenant" fallback.
  const location = useLocation();
  const justSignedOut = typeof window !== 'undefined'
    && new URLSearchParams(location.search).get('signed_out') === '1';
  // Land back at /?signed_out=1 after logout so AppLayout knows to show
  // the dedicated sign-out confirmation rather than auto-redirect to login.
  const logoutHref =
    typeof window !== 'undefined'
      ? `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(`${window.location.origin}/?signed_out=1`)}`
      : '/.auth/logout';
  const loginHref = withPostLoginRedirect('/.auth/login/aad');
  // Dedicated login href for the signed-out card. The standard
  // loginHref captures the CURRENT URL as the post-login redirect,
  // which on the signed-out card is `/?signed_out=1` — re-authenticating
  // would land the user right back on the signed-out card and trap them
  // there (caught in code review on PR #136). The explicit
  // post_login_redirect_uri=/ short-circuits withPostLoginRedirect's
  // current-URL capture (it leaves an existing target alone) so the
  // sign-back-in flow lands on a clean route.
  const signInAfterSignOutHref = withPostLoginRedirect('/.auth/login/aad?post_login_redirect_uri=/');

  const { baseItems, adminItems } = useMemo(() => {
    const canManageClientAccounts = hasClientAccountCapability(tenant);
    const canDeployToAppCatalog = hasDeployCapability(tenant);
    const canQualityReview = canReviewQuality(tenant);
    const canUseCapa = canUseCapaRegister(tenant);
    const canAccessRisk = canUseRisk(tenant);
    const riskVisibilitySource: VisibilitySource =
      canManageClientAccounts && canAccessRisk
        ? 'mixed'
        : 'audit';
    const capaVisibilitySource: VisibilitySource =
      canManageClientAccounts && canQualityReview
        ? 'mixed'
        : canManageClientAccounts
          ? 'employee'
          : 'audit';

    const base: NavItem[] = [
      { path: '/dashboard', label: 'Dashboard', icon: <HomeRegular />, visibilitySource: 'audit' },
      { path: '/docs', label: 'Docs', icon: <BookRegular />, visibilitySource: 'audit' },
      { path: '/template-library', label: 'Template Library', icon: <BookRegular />, visibilitySource: 'audit' },
      { path: '/support', label: 'Support', icon: <PersonSupportRegular />, visibilitySource: 'audit' },
      { path: '/account', label: 'Account', icon: <PersonRegular />, visibilitySource: 'audit' }
    ];

    // Tracker #121: every user who may use supplier collaboration sees the
    // page; administrators additionally see supplier and configuration tabs.
    if (tenant?.capabilities?.canUseSupplierCollaboration || tenant?.capabilities?.canManageSupplierCollaboration) {
      base.push({ path: '/collaboration', label: 'Supplier Quality Collaboration', icon: <DocumentDataRegular />, visibilitySource: 'audit' });
    }

    if (products.length > 0) {
      base.splice(1, 0, { path: '/products', label: 'Products', icon: <AppsRegular />, visibilitySource: 'audit' });
    }
    if (canAccessRisk) {
      base.splice(1, 0, { path: '/risk', label: 'Risk', icon: <DocumentDataRegular />, visibilitySource: riskVisibilitySource });
    }
    if (canUseCapa) {
      base.splice(2, 0, { path: '/capa-register', label: 'CAPA', icon: <DocumentDataRegular />, visibilitySource: capaVisibilitySource });
    }
    if (canDeployToAppCatalog) {
      base.push({ path: '/instances', label: 'Installations', icon: <BuildingRegular />, visibilitySource: 'mixed' });
    }

    const admin: NavItem[] = [];

    if (tenant?.capabilities?.canManagePermissions) {
      admin.push({ path: '/permissions', label: 'Permissions', icon: <SettingsRegular />, visibilitySource: canManageClientAccounts ? 'mixed' : 'audit' });
    }
    if (canManageClientAccounts) {
      admin.push({ path: '/client-accounts', label: 'Client Accounts', icon: <BuildingRegular />, visibilitySource: 'employee' });
      admin.push({ path: '/support-desk', label: 'Support Desk', icon: <PersonSupportRegular />, visibilitySource: 'employee' });
    }
    if (tenant?.capabilities?.canManageStaffRoles) {
      admin.push({ path: '/portal/admin/staff-roles', label: 'Staff Roles', icon: <PersonRegular />, visibilitySource: 'employee' });
    }
    if (tenant?.capabilities?.canManageScopes) {
      admin.push({ path: '/portal/admin/scopes', label: 'Scopes', icon: <DocumentDataRegular />, visibilitySource: canManageClientAccounts ? 'mixed' : 'audit' });
    }
    if (canDeployToAppCatalog) {
      admin.push({ path: '/portal/admin/spfx-releases', label: 'SPFx Releases', icon: <AppsRegular />, visibilitySource: 'mixed' });
    }
    if (canUseAiAdmin(tenant)) {
      admin.push({ path: '/portal/admin/ai-text', label: 'AI Text', icon: <SettingsRegular />, visibilitySource: tenant?.capabilities?.canUseCustomerSelfService ? 'mixed' : 'employee' });
      admin.push({ path: '/portal/admin/pii-detection', label: 'PII Detection', icon: <SettingsRegular />, visibilitySource: tenant?.capabilities?.canUseCustomerSelfService ? 'mixed' : 'employee' });
    }
    if (hasElevatedAccess(tenant?.roles, PORTAL_ROLES.marketingAdmin)) {
      admin.push({ path: '/portal/admin/marketing', label: 'Marketing', icon: <MegaphoneRegular />, visibilitySource: 'employee' });
      admin.push({ path: '/portal/admin/splash', label: 'Splash Content', icon: <SettingsRegular />, visibilitySource: 'employee' });
    }
    if (
      hasElevatedAccess(tenant?.roles, PORTAL_ROLES.evidenceManager) ||
      hasElevatedAccess(tenant?.roles, PORTAL_ROLES.documentManager)
    ) {
      admin.push({ path: '/portal/admin/evidence', label: 'Evidence Ops', icon: <DocumentDataRegular />, visibilitySource: 'employee' });
    }
    if (
      hasElevatedAccess(tenant?.roles, PORTAL_ROLES.documentManager) ||
      hasElevatedAccess(tenant?.roles, PORTAL_ROLES.marketingAdmin)
    ) {
      admin.push({ path: '/portal/admin/supplier-terms', label: 'Supplier Legal Content', icon: <DocumentDataRegular />, visibilitySource: 'employee' });
    }
    if (hasElevatedAccess(tenant?.roles, PORTAL_ROLES.globalAdmin)) {
      admin.push({ path: '/portal/admin/tests', label: 'Test Admin', icon: <BeakerRegular />, visibilitySource: 'employee' });
    }

    return { baseItems: base, adminItems: admin };
  }, [products.length, tenant]);

  const visibilityIcons = (source: VisibilitySource): Array<{ key: string; src: string; label: string }> => {
    if (source === 'employee') {
      return [{ key: 'employee', src: edinshallBadgeIconUrl, label: 'Staff/admin role' }];
    }
    if (source === 'audit') {
      return [{ key: 'audit', src: edaudBadgeIconUrl, label: 'Client-tenant role' }];
    }
    return [
      { key: 'employee', src: edinshallBadgeIconUrl, label: 'Staff/admin role' },
      { key: 'audit', src: edaudBadgeIconUrl, label: 'Client-tenant role' }
    ];
  };

  const visibilityLabel = (source: VisibilitySource): string => {
    if (source === 'employee') {
      return 'Shown by support/admin capability role';
    }
    if (source === 'mixed') {
      return 'Shown by multiple capability role paths';
    }
    return 'Shown by base customer access path';
  };

  // Dedicated post-sign-out landing. Takes priority over the
  // "can't load tenant" fallback because the 401 from /api/context after
  // sign-out would otherwise present the same screen — confusing the
  // user who just deliberately signed out.
  if (justSignedOut) {
    return (
      <div className="auth-fallback">
        <div className="auth-fallback-card">
          <div className="auth-fallback-brand">Edinshall Portal</div>
          <h2>You signed out of Edinshall</h2>
          <p>
            Your Edinshall portal session on this device is now cleared. You'll need to sign in again to use the portal.
          </p>
          <p>
            <strong>Your Microsoft 365 session is separate.</strong> You're still signed into Microsoft on this device. Use the Microsoft 365 account menu (top-right in any M365 app) to sign out of Microsoft entirely.
          </p>
          <a className="cta" href={signInAfterSignOutHref}>
            Sign back in
          </a>
        </div>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="auth-fallback">
        <div className="auth-fallback-card">
          <div className="auth-fallback-brand">Edinshall Portal</div>
          <h2>We could not load your tenant</h2>
          <p>
            Your sign-in looks valid, but the portal could not reach platform services to resolve
            your tenant details. Please sign in again.
          </p>
          <a className="cta" href={loginHref}>
            Login
          </a>
        </div>
      </div>
    );
  }

  // Destinations are grouped by who they serve, not by which capability
  // flag unlocked them. Staff-only tooling (visibilitySource === 'employee')
  // gets its own group so a customer admin's list is never padded with
  // Edinshall-internal destinations, and staff can see at a glance which
  // links act on the customer's organisation versus on Edinshall itself.
  const helpPaths = ['/docs', '/support', '/account'];
  const organisationPaths = ['/instances', '/products', '/template-library'];
  const organisationItems = [
    ...baseItems.filter(item => organisationPaths.includes(item.path)),
    ...adminItems.filter(item => item.visibilitySource !== 'employee')
  ];
  const staffItems = adminItems.filter(item => item.visibilitySource === 'employee');
  const navGroups = [
    { label: 'Daily work', items: baseItems.filter(item => !helpPaths.includes(item.path) && !organisationPaths.includes(item.path)) },
    { label: 'Organisation administration', items: organisationItems },
    { label: 'Edinshall staff', items: staffItems },
    { label: 'Help and account', items: baseItems.filter(item => helpPaths.includes(item.path)) }
  ].filter(group => group.items.length > 0);

  return (
    <div className={`app-shell ${collapsed ? 'nav-collapsed' : ''}`}>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <nav className={`nav ${collapsed ? 'collapsed' : ''}`} aria-label="Portal">
        <div className="nav-brand">
          <div className="nav-logo" aria-hidden="true">E</div>
          <div className="nav-brand-text">
            <span className="nav-brand-name">Edinshall Portal</span>
            <small>Client workspace</small>
          </div>
        </div>
        <button
          type="button"
          className="collapse-toggle"
          onClick={() => setCollapsed(current => !current)}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          {collapsed ? <ChevronRightRegular /> : <ChevronLeftRegular />}
        </button>
        {navGroups.map(group => <React.Fragment key={group.label}>
          <div className="nav-section">{group.label}</div>
          <ul className="nav-list">{group.items.map(item => <li key={item.path}>
            <NavLink to={item.path} aria-label={item.label} title={item.label} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span aria-hidden="true" className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </NavLink>
          </li>)}</ul>
        </React.Fragment>)}
      </nav>

      <header className="header">
        <div className="tenant-info">
          <strong>{tenant?.tenantName ?? 'Resolving tenant…'}</strong>
          <span className="email">{tenant?.userEmail ?? 'Loading user…'}</span>
        </div>
        <div className="header-actions">
          {tenant && tenant.environment !== 'prod' && (
            <div className="badge badge-environment" title="You are working in a non-production environment">
              {environmentLabel[tenant.environment] ?? tenant.environment} environment
            </div>
          )}
          {tenant?.planCode && (
            <div className="badge" title="Subscription plan">
              {tenant.planCode} plan
            </div>
          )}
          {tenant && <details className="account-access-details">
            <summary>Account and access</summary>
            <div className="account-access-content">
              <p>{getAccessPathLabel(tenant)}</p>
              {typeof tenant.coveredTenantCount === 'number' && <p>{tenant.coveredTenantCount} covered tenants</p>}
              <p>Subscription source: {tenant.subscriptionSource || 'Pending'}</p>
              <p>These are the capabilities that make each destination available to this account.</p>
              <ul>{[...baseItems, ...adminItems].map(item => <li key={item.path}>
                <strong>{item.label}:</strong> {visibilityLabel(item.visibilitySource)}
                <span aria-hidden="true" className="nav-visibility-icons">{visibilityIcons(item.visibilitySource).map(badge => <img key={badge.key} className="nav-visibility-icon" src={badge.src} alt="" />)}</span>
              </li>)}</ul>
              <NavLink to="/account">Open account</NavLink>
            </div>
          </details>}
          <LanguageSelector />
          <button
            type="button"
            className="btn secondary"
            onClick={() => setSignOutDialogOpen(true)}
          >
            {t('Header.SignOut')}
          </button>
        </div>
      </header>
      <SignOutDialog
        open={signOutDialogOpen}
        onCancel={() => setSignOutDialogOpen(false)}
        onConfirm={() => {
          // Navigate via window.location so the App Service /.auth/logout
          // handler can clear the cookie server-side before bouncing the
          // user to the post_logout_redirect_uri.
          if (typeof window !== 'undefined') {
            window.location.href = logoutHref;
          }
        }}
      />

      <main id="main-content" className="content" tabIndex={-1}>
        {tenant?.environment === 'dev' && (
          <div className="env-banner">Development environment — test tenants and preview services</div>
        )}
        {loading && <div className="badge-subtle">Loading platform data…</div>}
        {error && <div className="pill warning">{error}</div>}
        {children}
      </main>
    </div>
  );
};

